import React, { useState } from 'react';
import axios from 'axios';
import './PublicPatientRegistration.css';

function PublicPatientRegistration() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredPatient, setRegisteredPatient] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phone: '',
    email: null,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    medicalHistory: '',
    allergies: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Make direct API call to public endpoint without authentication
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.post(`${API_URL}/public/patients`, formData);

      // Store registered patient info and show success modal
      setRegisteredPatient(response.data.data);
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to register patient. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAnother = () => {
    // Reset form for new patient
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      phone: '',
      email: null,
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      medicalHistory: '',
      allergies: ''
    });
    setShowSuccessModal(false);
    setRegisteredPatient(null);
    setError('');
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <div className="public-registration-container">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={handleRegisterAnother}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="success-icon">✓</div>
              <h2>Registration Successful!</h2>
            </div>
            <div className="modal-body">
              <p>Patient has been registered successfully.</p>
              {registeredPatient && (
                <div className="patient-info">
                  <p><strong>Patient ID:</strong> {registeredPatient.patientId}</p>
                  <p><strong>Name:</strong> {registeredPatient.firstName} {registeredPatient.lastName}</p>
                  <p><strong>Phone:</strong> {registeredPatient.phone}</p>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-register-another" onClick={handleRegisterAnother}>
                Register Another Patient
              </button>
              <button className="btn-close" onClick={handleRegisterAnother}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="public-header">
        <img src="/logo.jpg" alt="Glow With SuperDoc" style={{ maxWidth: '180px', height: 'auto', marginBottom: '1rem' }} />
        <h1>Patient Registration</h1>
        <p>Please fill out the form below to register as a new patient</p>
      </header>

      <div className="public-registration-content">
        <div className="public-card">
          {error && <div className="error-message">❌ {error}</div>}

          <form onSubmit={handleSubmit}>
            <section className="form-section">
              <h2>Personal Information</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Enter first name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name (optional)"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </section>

            <section className="form-section">
              <h2>Address</h2>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleChange}
                  placeholder="Enter street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleChange}
                    placeholder="Enter state"
                  />
                </div>

                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleChange}
                    placeholder="Enter zip code"
                  />
                </div>
              </div>
            </section>

            <section className="form-section">
              <h2>Medical Information</h2>

              <div className="form-group">
                <label>Medical History</label>
                <textarea
                  name="medicalHistory"
                  value={formData.medicalHistory}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter any relevant medical history..."
                />
              </div>

              <div className="form-group">
                <label>Allergies</label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Enter any known allergies..."
                />
              </div>
            </section>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Registering...' : 'Register Patient'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <footer className="public-footer">
        <p>&copy; 2025 Glow With SuperDoc. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PublicPatientRegistration;
